import { Component, createRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./quillStyles.scss";

import Popup from "../common/Popup";
import ApiManager from "../../managers/ApiManager";
import { TABLES, TableManger } from "../../managers/TableManger";
import { DKInput, DKButton, INPUT_TYPE } from "deskera-ui-library";
import Utility from "../../utility/Utility";
import { AUTOMATION_TRIGGERS } from "../automation/AutomationConstants";
import AutomationManager from "../automation/AutomationManager";

class EmailEditor_Quill extends Component {
    constructor(props) {
        super(props);

        this.editorContainerRef = createRef();
        this.quillEditor = null;
        this.tableColumns = !Utility.isEmpty(this.props?.tableName) ? this.getAllTableColumnsForEditor(): this.getTableColumnsForEditor();
        this.triggerType = this.props?.triggerType
        this.state = {
            needHyperLinkPopup: false,
            linkText: "",
            linkURL: "",
            linkTextError: false,
            linkURLError: false,
        };
    }

    getTableColumnsForEditor() {
        const tableColumnsData = TableManger.getTableFilteredColumns(
            TABLES.CANDIDATE,
            (col) =>
                col.type !== INPUT_TYPE.SELECT && col.type !== INPUT_TYPE.MULTI_SELECT,
            true
        );

        const tableColumns = {};
        tableColumnsData.forEach((column) => {
            tableColumns[column.id] = `${column.name}`;
            if (column.columnCode === "name") {
                this.nameColumnId = column.id;
            }
        });

        return tableColumns;
    }

    getAllTableColumnsForEditor = () => {
        const tableColumnsData = TableManger.getAllTableFilteredColumns(
            this.props?.tableName,
            (col) =>
                col.type !== INPUT_TYPE.SELECT && col.type !== INPUT_TYPE.MULTI_SELECT,
            true
        );

        let tableColumns = {};
        tableColumnsData.forEach((column) => {
            switch(this.props?.triggerType) {
                case AUTOMATION_TRIGGERS.PEOPLE_DATE_ALERT:
                    tableColumns[column.columnCode] = `${column.name}`;
                    if (column.columnCode === "name") {
                        this.nameColumnId = column.columnCode;
                        tableColumns[column.columnCode] = `Name`;
                    }
                    break;

            }
        });

        if (this.props.tableName === TABLES.EMPLOYEE_LIST) {
            AutomationManager.filterEmployeeColumns().forEach((column) => {
                if (column.id === "name") {
                    this.nameColumnId = column.id;
                }

                tableColumns[column.id] = column.name;
            });
        }

        return tableColumns;
    }

    componentDidMount() {
        /* Remove existing toolbar inside editor container if any */
        this.editorContainerRef.current?.querySelector(".ql-toolbar")?.remove();

        try {
            this.setEditorCustomizations(Quill);
            this.quillEditor = new Quill(`#${this.props.editorID}`, {
                ...this.getQuillConfig(),
                bounds: `#${this.props.editorID}`,
                placeholder: "Compose an email...",
            });

            this.initializeBody();
            this.initializeAttachment();
            this.setupCustomFormatLabels();
        } catch (e) {
            console.log(e);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.body !== this.props.body || (this.props.selectedTemplateId && (this.props.selectedTemplateId !== nextProps.selectedTemplateId))) {
            this.initializeBody(nextProps);
        }
    }

    render() {
        return (
            <div ref={this.editorContainerRef} className="parent-width">
                <div
                    className="parent-width"
                    id={this.props.editorID}
                    style={{ height: this.props.height ? this.props.height : 300 }}
                    onDrop={this.handleFileDrop}
                ></div>
                {this.getHyperLinkPopup()}
            </div>
        );
    }

    //////////////////////////////////////////////////////////////////////////////////
    //////////////////////////  INITIALIZERS START ///////////////////////////////////
    //////////////////////////////////////////////////////////////////////////////////

    getQuillConfig = () => {
        return {
            theme: "snow",
            modules: {
                toolbar: {
                    container: [
                        [
                            { size: ["10px", "13px", "16px", "18px", "24px"] },
                            { color: [] },
                            { background: [] },
                        ],
                        ["bold", "italic", "underline", "strike", "blockquote"],
                        [{ list: "ordered" }, { list: "bullet" }, { align: [] }],
                        ["image", "link"],
                        /* Custom formats group below */
                        [
                            "dk_attachment",
                            { "email-placeholder": Object.keys(this.tableColumns) },
                        ],
                    ],
                    handlers: {
                        image: () => this.triggerAttachmentDialog(true),
                        link: this.showHyperLinkPopup,
                        dk_attachment: () => this.triggerAttachmentDialog(false),
                        "email-placeholder": this.handlePlaceholderInsertion,
                    },
                },
                clipboard: {
                    matchVisual: false,
                },
            },
        };
    }

    setEditorCustomizations = (quill) => {
        /* forcing quill editor to place alignment & size as inline-styles instead of classes */
        const AlignStyle = quill.import("attributors/style/align");
        quill.register({ "formats/align": AlignStyle }, true);

        const SizeStyle = quill.import("attributors/style/size");
        SizeStyle.whitelist = ["10px", "13px", "16px", "18px", "24px"];
        quill.register({ "formats/size": SizeStyle }, true);

        const Block = quill.import("blots/block");
        Block.tagName = "div";
        quill.register(Block, true);

        this.setFieldListFormatter(quill);
    };

    initializeBody = (nextProps = this.props) => {
        let { body } = nextProps;

        if (body) {
            if (this.nameColumnId) {
                body = body.replace(
                    `%name%`,
                    `<span class="custom_email_placeholder" data-id=${this.nameColumnId}>%${this.nameColumnId}%</span>`
                );
            }

            this.quillEditor.clipboard.dangerouslyPasteHTML(0, body);
            this.quillEditor.blur();

            /* updating email-placeholders text if present in body before hand */
            const placeholders =
                this.editorContainerRef.current?.getElementsByClassName(
                    "custom_email_placeholder"
                );
            placeholders &&
                Array.from(placeholders).forEach((placeholder) => {
                    const dataId = placeholder.getAttribute("data-id");
                    const columnName = this.tableColumns[dataId] || "";
                    placeholder.innerHTML = `<span contenteditable="false">${columnName}</span>`;
                });
        }
    };

    //////////////////////////////////////////////////////////////////////////////////
    //////////////////////////  CUSTOM PLACEHOLDERS START ////////////////////////////
    //////////////////////////////////////////////////////////////////////////////////

    setFieldListFormatter = (quill) => {
        const Embed = quill.import("blots/embed");

        class EmailPlaceholder extends Embed {
            static create(value) {
                let node = super.create(value);
                node.setAttribute("data-id", value.dataId);
                node.textContent = value.displayName;
                return node;
            }

            static value(node) {
                return {
                    dataId: node.getAttribute("data-id"),
                    displayName: node.textContent,
                };
            }
        }

        EmailPlaceholder.blotName = "email-placeholder";
        EmailPlaceholder.className = "custom_email_placeholder";
        EmailPlaceholder.tagName = "SPAN";

        quill.register({ "formats/email-placeholder": EmailPlaceholder }, true);
    };

    setupCustomFormatLabels = () => {
        const placeholderFormats = this.editorContainerRef.current.querySelectorAll(
            ".ql-email-placeholder .ql-picker-item"
        );
        Array.from(placeholderFormats).forEach((placeholderElement) => {
            const columnID = placeholderElement.getAttribute("data-value");
            placeholderElement.textContent = this.tableColumns[columnID];
        });

        this.quillEditor.root.addEventListener("click", this.editorClickHandler);
    };

    handlePlaceholderInsertion = (columnId) => {
        if (columnId) {
            const range = this.quillEditor.getSelection(true);

            if (range.length) {
                const selectedText = this.quillEditor.getText(
                    range.index,
                    range.length
                );
                this.quillEditor.deleteText(range.index, selectedText.trimEnd().length);
            }

            this.quillEditor.insertEmbed(range.index, "email-placeholder", {
                dataId: columnId,
                displayName: this.tableColumns[columnId],
            });

            this.quillEditor.insertText(range.index + 1, " ");
            this.quillEditor.setSelection(range.index + 2);
        }
    };

    editorClickHandler = (event) => {
        const target = event.target;
        const emailPlaceholderElement = target?.closest(
            ".custom_email_placeholder"
        );

        const placeholderButton = this.editorContainerRef.current?.querySelector(
            ".ql-email-placeholder .ql-picker-label"
        );

        if (!placeholderButton) {
            return;
        } else if (emailPlaceholderElement) {
            placeholderButton.classList.add("ql-active");
            const dataId = emailPlaceholderElement.getAttribute("data-id");
            placeholderButton.setAttribute("data-value", dataId);

            placeholderButton.setAttribute("data-title", this.tableColumns[dataId]);
        } else {
            placeholderButton.classList.remove("ql-active");
        }
    };

    initializeAttachment = () => {
        const attachmentButton =
            this.editorContainerRef.current?.querySelector(".ql-dk_attachment");
        if (attachmentButton) {
            attachmentButton.innerHTML = `
      <svg viewBox="0 0 512 512">
        <path class="ql-even ql-stroke" d="M467.076,68.86c-59.902-59.902-156.846-59.896-216.741,0L34.919,284.276c-46.558,46.557-46.558,122.312,0,168.87
          c46.57,46.571,122.326,46.544,168.87,0L419.205,237.73c33.36-33.36,33.36-87.64,0-121c-33.359-33.361-87.64-33.361-121,0
          L114.478,300.457c-6.975,6.975-6.975,18.285,0,25.259c6.975,6.975,18.285,6.975,25.259,0l183.727-183.727
          c19.432-19.432,51.05-19.432,70.481,0c19.431,19.432,19.431,51.05,0,70.481L178.53,427.887c-32.71,32.71-85.646,32.706-118.352,0
          c-15.806-15.806-24.511-36.821-24.511-59.175s8.706-43.369,24.511-59.176L275.594,94.119c45.94-45.94,120.287-45.934,166.222,0
          c45.827,45.828,45.827,120.395,0,166.222l-95.741,95.741c-6.975,6.975-6.975,18.284,0,25.259s18.285,6.975,25.259,0l95.741-95.741
          C526.978,225.7,526.971,128.754,467.076,68.86z"/>
      </svg>`;
        }
    };

    handleFileDrop = (event) => {
        event.preventDefault();
        const files = event.dataTransfer.files;
        if (files?.length) {
            Array.from(files).forEach((file) => {
                this.uploadAttachmentToAWS(
                    file,
                    new RegExp("^(image/)").test(file.type)
                );
            });
        }
    };

    //////////////////////////////////////////////////////////////////////////////////
    //////////////////////////  IMAGE HANDLERS START /////////////////////////////////
    //////////////////////////////////////////////////////////////////////////////////

    triggerAttachmentDialog = (isImage) => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.accept = isImage ? "image/*" : "*";
        input.addEventListener("change", (e) => {
            if (e.target.files.length > 0) {
                this.uploadAttachmentToAWS(e.target.files[0], isImage);
            }
        });
        input.click();
    };

    onAttachmentUploaded = (url, fileData, isImage) => {
        if (isImage) {
            const range = this.quillEditor.getSelection(true);
            this.quillEditor.insertEmbed(range.index, "image", url);
        } else {
            this.setState(
                {
                    linkText: fileData?.name || `Untitled`,
                    linkURL: url,
                },
                this.onInsertHyperLink
            );
        }
    };

    uploadAttachmentToAWS = (fileData, isImage) => {
        ApiManager.getUploadFileURL(
            fileData,
            (url) => this.onAttachmentUploaded(url, fileData, isImage),
            (err) => { },
            "CRM_ATTACHMENT_EMAILS"
        );
    };

    //////////////////////////////////////////////////////////////////////////////////
    //////////////////////////  HYPER-LINK HANDLERS START ////////////////////////////
    //////////////////////////////////////////////////////////////////////////////////
    showHyperLinkPopup = (e) => {
        const range = this.quillEditor.getSelection();
        let linkText = "";

        if (range && range.length) {
            linkText = this.quillEditor.getText(range.index, range.length);
        }

        this.setState((prev) => ({
            needHyperLinkPopup: true,
            linkText: linkText,
        }));
    };

    closeHyperLinkPopup = () => {
        this.setState({
            needHyperLinkPopup: false,
            linkText: "",
            linkURL: "",
            linkTextError: false,
            linkURLError: false,
        });
    };

    getAbsoluteURL(url) {
        const regex = /^https?:\/{2}(\w)?/i;
        const mailLinkRegex = /^mailto:\w/i;
        if (!url || regex.test(url) || mailLinkRegex.test(url)) return url;

        return `//${url}`;
    }

    onInsertHyperLink = () => {
        const { linkText, linkURL } = this.state;
        if (linkText && linkURL) {
            const range = this.quillEditor.getSelection(true);
            if (range && range.length) {
                const selectedText = this.quillEditor.getText(
                    range.index,
                    range.length
                );
                this.quillEditor.deleteText(range.index, selectedText.trimEnd().length);
            }

            this.quillEditor.insertText(
                range.index,
                linkText.trim(),
                "link",
                this.getAbsoluteURL(linkURL)
            );

            this.quillEditor.setSelection(range.index, 0);

            this.closeHyperLinkPopup();
        } else {
            this.setState({
                linkTextError: linkText ? false : true,
                linkURLError: linkURL ? false : true,
            });
        }
    };

    getHyperLinkPopup = () => {
        return this.state.needHyperLinkPopup ? (
            <Popup
                allowClose={true}
                onClose={this.closeHyperLinkPopup}
                title="Insert Link"
            >
                <div className="column">
                    <DKInput
                        className="mt-xl"
                        title="Text"
                        type={INPUT_TYPE.TEXT}
                        value={this.state.linkText}
                        canValidate={this.state.linkTextError}
                        // invalid={this.state.linkTextError}
                        onChange={(val) => this.setState({ linkText: val })}
                    />
                    <DKInput
                        className="mt-r"
                        title="URL"
                        type={INPUT_TYPE.TEXT}
                        value={this.state.linkURL}
                        canValidate={this.state.linkURLError}
                        // invalid={this.state.linkURLError}
                        onChange={(val) => this.setState({ linkURL: val })}
                    />
                    <div className="row justify-content-end align-items-center mt-xl">
                        <a
                            className="justify-self-start"
                            href={this.getAbsoluteURL(this.state.linkURL)}
                            style={{
                                marginRight: "auto",
                            }}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Preview Link
                        </a>
                        <DKButton
                            title="Cancel"
                            className="bg-white border-m mr-s"
                            onClick={this.closeHyperLinkPopup}
                        />
                        <DKButton
                            title="Save"
                            className="bg-button text-white"
                            onClick={this.onInsertHyperLink}
                        />
                    </div>
                </div>
            </Popup>
        ) : null;
    };
}

export default EmailEditor_Quill;
